import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  meQueryOptions,
  periodsListQueryOptions,
  apAgingQueryOptions,
  arAgingQueryOptions,
  approvalRequestsListQueryOptions,
  projectsListQueryOptions,
  bankAccountsListQueryOptions,
  wipQueryOptions,
  auditLogQueryOptions,
  payRunsQueryOptions,
  myEntitiesQueryOptions,
  implementationInstalledQueryOptions,
  saasMrrQueryOptions,
  saasArrQueryOptions,
  saasSubscriptionsQueryOptions,
  saasSnapshotsQueryOptions,
  saasRecognitionSchedulesQueryOptions,
  saasBurnQueryOptions,
  saasLtvCacQueryOptions,
  certifiedPayrollAlertsQueryOptions,
  overdraftAlertsQueryOptions,
  apIntakeListQueryOptions,
  tmTicketsListQueryOptions,
  tmInvoicesListQueryOptions,
  equipmentItemsQueryOptions,
  equipmentUnpostedTimeEntriesQueryOptions,
  openIntakeItems,
} from "@/lib/queries";
import { api, setToken } from "@/lib/api";
import { setNavLayout } from "@/lib/navLayouts";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CasinoOutlinedIcon from "@mui/icons-material/CasinoOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { dashboardModes, effectiveDashboardMode } from "@/lib/dashboardModes";
import { HospitalityDashboardMetrics } from "@/components/hospitality/DashboardMetrics";
import { OperatingWorkspace } from "@/components/workspace/OperatingWorkspace";
import { workspaceOffered } from "@/lib/workspaceQueue";
import type {
  WipRow,
  AuditLogRow,
  PayRun,
  BankAccount,
  AvailableEntity,
  MetricsSnapshot,
  RecognitionSchedule,
  Subscription,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/")({
  component: HomePage,
});

// ── Formatters ────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtCompact(n: number) {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return fmt(n);
}

function greet(name: string) {
  const h = new Date().getHours();
  const salutation = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  return `${salutation}, ${name.split(" ")[0]}`;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── Quick-link modules ────────────────────────────────────────────────────────

type ModuleLink = { label: string; href: string; icon: typeof ReceiptLongOutlinedIcon };

const MODULES: ModuleLink[] = [
  { label: "AP",              href: "/ap",              icon: ReceiptLongOutlinedIcon },
  { label: "AR",              href: "/ar",              icon: RequestQuoteOutlinedIcon },
  { label: "Projects",        href: "/projects",        icon: ConstructionOutlinedIcon },
  { label: "Job Cost",        href: "/reports",         icon: AssessmentOutlinedIcon },
  { label: "Payroll",         href: "/payroll/pay-runs", icon: PaymentsOutlinedIcon },
  { label: "Purchase Orders", href: "/purchase-orders", icon: ShoppingCartOutlinedIcon },
  { label: "Subcontracts",    href: "/subcontracts",    icon: AssignmentOutlinedIcon },
  { label: "Vendors",         href: "/vendors",          icon: StorefrontOutlinedIcon },
  { label: "Lien Waivers",    href: "/lien-waivers",    icon: HistoryEduOutlinedIcon },
  { label: "Contracts",       href: "/contracts",       icon: HandshakeOutlinedIcon },
  { label: "GL / Journals",   href: "/gl",              icon: MenuBookOutlinedIcon },
  { label: "Reports",         href: "/reports",         icon: SummarizeOutlinedIcon },
];

const SAAS_MODULES: ModuleLink[] = [
  { label: "Subscriptions", href: "/saas",                icon: AutorenewOutlinedIcon },
  { label: "Plans",         href: "/saas",                icon: ListAltOutlinedIcon },
  { label: "Recognition",   href: "/recognition",         icon: CalendarMonthOutlinedIcon },
  { label: "Metrics",       href: "/saas/metrics",        icon: TrendingUpOutlinedIcon },
  { label: "AR Invoices",   href: "/ar",                  icon: RequestQuoteOutlinedIcon },
  { label: "Recurring JEs", href: "/recurring-journals",  icon: RepeatOutlinedIcon },
  { label: "Expenses",      href: "/expenses",            icon: ReceiptOutlinedIcon },
  { label: "GL / Journals", href: "/gl",                  icon: MenuBookOutlinedIcon },
];

const HOSPITALITY_MODULES: ModuleLink[] = [
  { label: "Night Audit",     href: "/hospitality/night-audit",     icon: BedtimeOutlinedIcon },
  { label: "Title 31 / BSA",  href: "/hospitality/title-31",        icon: ShieldOutlinedIcon },
  { label: "Gaming Tax",      href: "/hospitality/gaming-tax",      icon: CasinoOutlinedIcon },
  { label: "Revenue Centers", href: "/hospitality/revenue-centers", icon: HotelOutlinedIcon },
  { label: "AP",              href: "/ap",                          icon: ReceiptLongOutlinedIcon },
  { label: "AR",              href: "/ar",                          icon: RequestQuoteOutlinedIcon },
  { label: "Payroll",         href: "/payroll/pay-runs",            icon: PaymentsOutlinedIcon },
  { label: "Vendors",         href: "/vendors",                     icon: StorefrontOutlinedIcon },
  { label: "GL / Journals",   href: "/gl",                          icon: MenuBookOutlinedIcon },
  { label: "Reports",         href: "/reports",                     icon: SummarizeOutlinedIcon },
];

// ── Pay-run status helpers ────────────────────────────────────────────────────

const PAY_RUN_STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  calculated: "Calculated",
  approved: "Approved",
  paid: "Paid",
  posted: "Posted",
  voided: "Voided",
};

const PAY_RUN_STATUS_COLOR: Record<string, string> = {
  draft:      "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  calculated: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  approved:   "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  paid:       "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  posted:     "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  voided:     "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300",
};

// Friendly labels for audit log table names
function auditTableLabel(t: string) {
  const map: Record<string, string> = {
    ap_invoice_intake: "Bill intake",
    ap_invoices: "AP Invoice", ar_invoices: "AR Invoice",
    payments: "Payment", journals: "Journal",
    purchase_orders: "Purchase Order", subcontracts: "Subcontract",
    projects: "Project", payroll_pay_runs: "Pay Run",
    lien_waivers: "Lien Waiver", contracts: "Contract",
    parties: "Vendor / Party", bank_accounts: "Bank Account",
    tm_tickets: "T&M Ticket",
    users: "Users",
  };
  return map[t] ?? t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Past-tense verb for the activity feed ("inserted", not "insertd"). */
function auditActionPast(action: string): string {
  switch (action.toUpperCase()) {
    case "INSERT": return "inserted";
    case "UPDATE": return "updated";
    case "DELETE": return "deleted";
    default: return action.toLowerCase();
  }
}

/**
 * Authentication bookkeeping is useful in the full audit log, but it is not
 * business activity and can crowd every other event out of the dashboard.
 */
function isDashboardActivity(row: AuditLogRow): boolean {
  return !(
    row.table_name === "users" &&
    row.action === "UPDATE" &&
    row.changed_fields?.length === 1 &&
    row.changed_fields[0] === "last_login_at"
  );
}

function auditValue(row: AuditLogRow, ...keys: string[]): string | null {
  const data = row.new_data ?? row.old_data;
  if (!data) return null;
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

/** A human-readable identifier so separate records do not look duplicated. */
function auditRecordLabel(row: AuditLogRow): string | null {
  switch (row.table_name) {
    case "ap_invoice_intake":
      return auditValue(row, "source_file_original_name", "source_email_subject");
    case "ap_invoices":
    case "ar_invoices":
      return auditValue(row, "invoice_number", "number");
    case "projects":
      return auditValue(row, "name", "project_number", "code");
    case "parties":
      return auditValue(row, "name", "code");
    case "journals":
      return auditValue(row, "journal_number", "number", "description");
    case "payments":
      return auditValue(row, "payment_number", "reference_number", "number");
    case "purchase_orders":
      return auditValue(row, "po_number", "number");
    case "subcontracts":
      return auditValue(row, "subcontract_number", "number");
    case "contracts":
      return auditValue(row, "contract_number", "name");
    case "users":
      return auditValue(row, "full_name", "email");
    default:
      return auditValue(row, "name", "number", "code");
  }
}

function dashboardActivityRows(
  rows: AuditLogRow[] | undefined,
  limit: number,
): AuditLogRow[] {
  return (rows ?? []).filter(isDashboardActivity).slice(0, limit);
}

const ACTIVITY_CLEARED_KEY = "ledger.dashboard.activityClearedAt";

function useClearedActivityAt(): [string | null, () => void] {
  const [clearedAt, setClearedAt] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVITY_CLEARED_KEY);
    } catch {
      return null;
    }
  });

  const clearAll = () => {
    const now = new Date().toISOString();
    try {
      localStorage.setItem(ACTIVITY_CLEARED_KEY, now);
    } catch {
      // localStorage unavailable — still update in-memory state
    }
    setClearedAt(now);
  };

  return [clearedAt, clearAll];
}

function activityAfterClear(rows: AuditLogRow[], clearedAt: string | null): AuditLogRow[] {
  if (!clearedAt) return rows;
  const cutoff = new Date(clearedAt).getTime();
  return rows.filter((row) => new Date(row.at).getTime() > cutoff);
}

function RecentActivitySection({
  auditRows,
  limit,
}: {
  auditRows: AuditLogRow[] | undefined;
  limit: number;
}) {
  const [clearedAt, clearAll] = useClearedActivityAt();
  const recentActivity = activityAfterClear(
    dashboardActivityRows(auditRows, limit),
    clearedAt,
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-3">
        <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          Recent activity
        </h2>
        <div className="flex items-center gap-3 flex-shrink-0">
          {recentActivity.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              Clear all
            </button>
          )}
          <Link
            to="/audit-log"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline no-underline"
          >
            View all history
          </Link>
        </div>
      </div>
      <Card>
        {recentActivity.length === 0 ? (
          <p className="px-4 py-5 text-sm text-slate-400 dark:text-slate-500">
            {clearedAt ? "You're all caught up." : "No activity recorded yet."}
          </p>
        ) : (
          recentActivity.map((row) => (
            <RecentActivityItem key={row.id} row={row} />
          ))
        )}
      </Card>
    </div>
  );
}

/**
 * Where a recent-activity row should go when clicked.
 * Prefer the live record screen; fall back to the list for that module,
 * then to the audit log.
 */
function auditActivityHref(tableName: string, rowId: string | null): string {
  const id = rowId?.trim() || "";
  switch (tableName) {
    case "ap_invoice_intake":
      return id ? `/bills-intake?focus=${encodeURIComponent(id)}` : "/bills-intake";
    case "ap_invoices":
      return id ? `/invoices/ap/${id}` : "/ap";
    case "ar_invoices":
      return id ? `/invoices/ar/${id}` : "/ar";
    case "payments":
      return id ? `/payments/${id}` : "/payments";
    case "journals":
      return id ? `/journals/${id}` : "/journals";
    case "projects":
      return id ? `/projects/${id}` : "/projects";
    case "parties":
      return id ? `/vendors/${id}` : "/vendors";
    case "bank_accounts":
      return id ? `/bank-accounts/${id}` : "/bank-accounts";
    case "purchase_orders":
      return "/ap";
    case "subcontracts":
      return "/vendors";
    case "users":
      return "/settings/team";
    case "payroll_pay_runs":
      return "/payroll-l2";
    case "tm_tickets":
      return "/ap";
    default:
      return "/audit-log";
  }
}

// ── Dashboard mode hook ───────────────────────────────────────────────────────

function useDashboardMode(): [string, (m: string) => void] {
  const [mode, setMode] = useState(() =>
    localStorage.getItem("bynaus_dashboard_mode") ?? "construction"
  );
  const set = (m: string) => {
    localStorage.setItem("bynaus_dashboard_mode", m);
    setMode(m);
  };
  return [mode, set];
}

// ── Home view hook (Minimal | Simple | Detailed) ─────────────────────────────
// "Detailed" is today's dashboard, unchanged. "Simple" strips it to banners,
// four KPIs, Needs attention, and Recent activity. "Minimal" is Simple with
// the page-header row removed entirely — the view switcher moves inside the
// dashboard. Persisted per browser so the choice sticks — same pattern as
// the dashboard-mode toggle above.

export type HomeView = "minimal" | "simple" | "detailed";

function useHomeView(): [HomeView, (v: HomeView) => void] {
  const [view, setView] = useState<HomeView>(() => {
    const raw = localStorage.getItem("ledger.home.view");
    return raw === "minimal" || raw === "simple" ? raw : "detailed";
  });
  const set = (v: HomeView) => {
    localStorage.setItem("ledger.home.view", v);
    setView(v);
  };
  return [view, set];
}

// One source of truth for the switcher, wherever it renders (header row in
// Simple/Detailed, inline in the dashboard in Minimal).
const HOME_VIEW_OPTIONS = [
  { key: "minimal", label: "Minimal", title: "No header row — just the work: KPIs, Needs attention, Recent activity" },
  { key: "simple", label: "Simple", title: "Just what needs action: KPIs, Needs attention, Recent activity" },
  { key: "detailed", label: "Detailed", title: "The full dashboard: cash, pay runs, WIP, modules" },
] as const;

function HomeViewSwitcher({ view, onChange }: { view: HomeView; onChange: (v: HomeView) => void }) {
  return (
    <div className="flex items-center gap-1 bg-slate-100 dark:bg-gray-800 rounded-lg p-0.5 flex-shrink-0">
      {HOME_VIEW_OPTIONS.map(({ key, label, title }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          title={title}
          className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
            view === key
              ? "bg-white dark:bg-gray-700 shadow-sm text-slate-900 dark:text-slate-100"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Home layout hook (Workspace | Classic dashboard) ─────────────────────────
// Construction companies land on the operating workspace; "Classic" is the
// dashboard below. Persisted per browser, like the dashboard mode.

type HomeLayout = "workspace" | "classic";

const HOME_LAYOUT_OPTIONS = [
  { key: "workspace", label: "Workspace" },
  { key: "classic", label: "Classic dashboard" },
] as const;

function useHomeLayout(): [HomeLayout, (l: HomeLayout) => void] {
  const [layout, setLayout] = useState<HomeLayout>(() => {
    try {
      return localStorage.getItem("ledger.home.layout") === "classic" ? "classic" : "workspace";
    } catch {
      return "workspace";
    }
  });
  const set = (l: HomeLayout) => {
    try {
      localStorage.setItem("ledger.home.layout", l);
    } catch {
      // The choice still applies for this visit when storage is unavailable.
    }
    setLayout(l);
  };
  return [layout, set];
}

// ── Subscription status chip colors ──────────────────────────────────────────

function subStatusChipClass(status: string): string {
  switch (status) {
    case "active":    return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
    case "trialing":  return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
    case "past_due":  return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
    case "paused":    return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
    case "canceled":  return "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300";
    case "expired":   return "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300";
    default:          return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
  }
}

// ── HomePage ──────────────────────────────────────────────────────────────────

function HomePage() {
  const qc = useQueryClient();
  const [dashMode, setDashMode] = useDashboardMode();
  const [homeView, setHomeView] = useHomeView();
  // "⋯" overflow menu — holds everything evicted from the header row.
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

  const { data: me }       = useQuery(meQueryOptions());
  const { data: entities } = useQuery(myEntitiesQueryOptions());
  const curEntity = entities?.find((e) => e.is_current);
  const constructionOn = curEntity?.construction_enabled ?? true;
  const hospitalityOn = (curEntity as { hospitality_enabled?: boolean } | undefined)?.hospitality_enabled ?? false;
  // Pack-aware dashboard modes: only what the server confirmed for this
  // company (industry layouts, active packs), and auto-correct a stored
  // preference that isn't available here.
  const { data: installed, isPending: installedPending } = useQuery(implementationInstalledQueryOptions());
  const DASH_MODES = dashboardModes({
    constructionOn,
    hospitalityOn,
    industryLayouts: installed?.industry_layouts,
    activePacks: installed?.active_packs,
  });
  const effectiveMode = effectiveDashboardMode(dashMode, DASH_MODES);
  const chooseDashboardMode = (mode: string) => {
    setDashMode(mode);
    if (mode === "concrete_pumping") setNavLayout("concrete_pumping");
  };
  // The operating workspace is the construction home (WORKSPACE-LIVE-001);
  // the classic dashboard stays one click away in the ⋯ menu.
  const [homeLayout, setHomeLayout] = useHomeLayout();
  const workspaceAvailable = workspaceOffered(installed?.active_packs);
  const showWorkspace = workspaceAvailable && effectiveMode === "construction" && homeLayout === "workspace";
  // Until the packs are known, a workspace home shows nothing rather than
  // flashing the classic dashboard (and firing its requests) first.
  const homePending = installedPending && homeLayout === "workspace";
  const { data: setup } = useQuery({
    queryKey: ["onboarding", "setup-status"],
    queryFn: () => api<{ done_count: number; total: number; complete: boolean; dismissed: boolean }>(
      "/api/onboarding/setup-status"),
    staleTime: 30_000,
  });
  // One-time setup-complete celebration (per browser).
  const [celebrated, setCelebrated] = useState(
    () => localStorage.getItem("bynaus_setup_celebrated") === "1");
  const dismissCelebration = () => {
    localStorage.setItem("bynaus_setup_celebrated", "1");
    setCelebrated(true);
  };

  const switchMutation = useMutation({
    mutationFn: (entity_id: string) =>
      api<{ access_token: string }>("/api/auth/switch-entity", {
        method: "POST", body: { entity_id },
      }),
    onSuccess: (resp) => {
      setToken(resp.access_token);
      qc.clear();
    },
  });

  const allEntities = entities ?? [];
  const name = me?.full_name ?? "";
  const openAgent = () => window.dispatchEvent(new Event("bynaus:open-agent"));
  // Open the agent pane and have the CFO offer the guided tour (full / quick / cash-flow).
  const startTour = () =>
    window.dispatchEvent(new CustomEvent("bynaus:open-agent", {
      detail: { prompt: "I just signed up — give me a tour of the app.", autoSend: true },
    }));
  // Hand the user to the CFO Setup Coach (guides + verifies real setup steps).
  const startSetup = () =>
    window.dispatchEvent(new CustomEvent("bynaus:open-agent", {
      detail: { prompt: "Help me finish setting up Ledger Core — coach me through what's left.", autoSend: true },
    }));

  // Overflow "⋯" menu — companies, dashboard mode, home layout, learning,
  // tour, setup. Rendered in the header row, or inside the workspace.
  const homeMenu = (
    <div className="relative">
      <button
        onClick={() => setHeaderMenuOpen((v) => !v)}
        title="Companies, dashboard mode, learning, tour, setup"
        aria-haspopup="menu"
        aria-expanded={headerMenuOpen}
        className="relative flex items-center px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
      >
        ⋯
        {setup && !setup.complete && !setup.dismissed && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500 border-2 border-white dark:border-gray-900" />
        )}
      </button>
      {headerMenuOpen && (
        <>
          {/* click-away backdrop */}
          <div className="fixed inset-0 z-30" onClick={() => setHeaderMenuOpen(false)} />
          <div className="absolute right-0 mt-1.5 w-72 z-40 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-2">
            {/* Companies */}
            {allEntities.length > 0 && (
              <div className="px-3 pb-2 border-b border-slate-100 dark:border-gray-700">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5">
                  Company
                </div>
                <div className="flex flex-col gap-0.5">
                  {allEntities.map((e: AvailableEntity) => (
                    <button
                      key={e.id}
                      onClick={() => {
                        if (!e.is_current) switchMutation.mutate(e.id);
                        setHeaderMenuOpen(false);
                      }}
                      disabled={switchMutation.isPending}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] text-left transition-colors ${
                        e.is_current
                          ? "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-medium"
                          : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: e.primary_color ?? "#94a3b8" }}
                      />
                      <span className="flex-1 truncate">{e.display_name}</span>
                      {e.is_current && <span className="text-[11px]">✓</span>}
                    </button>
                  ))}
                  <Link
                    to="/settings/entities"
                    onClick={() => setHeaderMenuOpen(false)}
                    className="px-2 py-1.5 rounded-md text-[12px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 no-underline"
                  >
                    + Add company
                  </Link>
                </div>
              </div>
            )}
  
            {/* Dashboard mode */}
            {DASH_MODES.length > 1 && (
              <div className="px-3 py-2 border-b border-slate-100 dark:border-gray-700">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5">
                  Dashboard
                </div>
                <div className="grid grid-cols-2 gap-1 bg-slate-100 dark:bg-gray-700 rounded-lg p-0.5">
                  {DASH_MODES.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => chooseDashboardMode(key)}
                      className={`flex-1 px-2 py-1 rounded-md text-[11px] font-medium transition-all ${
                        effectiveMode === key
                          ? "bg-white dark:bg-gray-600 shadow-sm text-slate-900 dark:text-slate-100"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Home layout — the operating workspace or the classic dashboard */}
            {workspaceAvailable && effectiveMode === "construction" && (
              <div className="px-3 py-2 border-b border-slate-100 dark:border-gray-700">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5">
                  Home
                </div>
                <div className="grid grid-cols-2 gap-1 bg-slate-100 dark:bg-gray-700 rounded-lg p-0.5">
                  {HOME_LAYOUT_OPTIONS.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => { setHomeLayout(key); setHeaderMenuOpen(false); }}
                      aria-pressed={homeLayout === key}
                      className={`flex-1 px-2 py-1 rounded-md text-[11px] font-medium transition-all ${
                        homeLayout === key
                          ? "bg-white dark:bg-gray-600 shadow-sm text-slate-900 dark:text-slate-100"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Learn & set up */}
            <div className="px-3 pt-2 flex flex-col gap-0.5">
              {setup && !setup.complete && !setup.dismissed && (
                <button
                  onClick={() => { setHeaderMenuOpen(false); startSetup(); }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] text-left text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors"
                >
                  <RocketLaunchOutlinedIcon className="text-[17px]" />
                  <span>Finish setup · {setup.done_count}/{setup.total}</span>
                </button>
              )}
              <button
                onClick={() => { setHeaderMenuOpen(false); startTour(); }}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] text-left text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ExploreOutlinedIcon className="text-[17px]" />
                <span>Take the tour</span>
              </button>
              <Link
                to="/help"
                onClick={() => setHeaderMenuOpen(false)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 no-underline transition-colors"
              >
                <SchoolOutlinedIcon className="text-[17px]" />
                <span>Learning</span>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">

      {/* ── Page header — one quiet row ──
          Greeting · view switcher · Ask Bynaus · "⋯".
          Everything that used to crowd this row (company chips, dashboard
          mode, Learning, Set up n/N, Take the tour) lives inside the ⋯ menu.
          An amber dot on ⋯ signals unfinished setup without shouting.
          In the Minimal view the whole row is gone — the top bar's Ask
          button and entity pill already cover its jobs, and the view
          switcher renders inside the dashboard instead. */}
      {!showWorkspace && !homePending && !(homeView === "minimal" && effectiveMode !== "saas") && (
      <div className="bg-white border-b border-slate-200 px-6 py-2.5 dark:bg-gray-900 dark:border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-4">
          {/* Greeting */}
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex-shrink-0">
            {name ? greet(name) : "Home"}
          </h1>

          {/* Home view switcher — construction/hospitality dashboards only;
              SaaS has its own layout. */}
          {effectiveMode !== "saas" && (
            <HomeViewSwitcher view={homeView} onChange={setHomeView} />
          )}

          {switchMutation.isPending && (
            <span className="text-[11px] text-slate-400 dark:text-slate-500 animate-pulse">Switching…</span>
          )}

          {/* Push buttons to the right */}
          <div className="flex-1" />

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={openAgent}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
            >
              <span>✦</span>
              <span>Ask Bynaus</span>
            </button>

            {/* Overflow — everything else */}
            {homeMenu}
          </div>
        </div>
      </div>
      )}

      {/* One-time 🎉 when the CFO Setup Coach reports every step done/skipped. */}
      {setup?.complete && !celebrated && (
        <div className="mx-6 mt-3 flex items-center justify-between gap-4 rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-gradient-to-r from-emerald-50 to-amber-50 dark:from-emerald-950/40 dark:to-amber-950/30 px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <div>
              <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                You're all set up!
              </div>
              <div className="text-[12px] text-emerald-700/80 dark:text-emerald-300/70">
                Every setup step is done. Your CFO is ready to run the books — ask anytime.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openAgent}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
            >
              Ask your CFO
            </button>
            <button
              onClick={dismissCelebration}
              title="Dismiss"
              className="px-2 py-1.5 rounded-lg text-emerald-700/70 dark:text-emerald-300/70 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {homePending ? (
        <div className="flex-1 bg-[#f4f5f1] dark:bg-gray-950" aria-busy="true" />
      ) : showWorkspace ? (
        <OperatingWorkspace menu={homeMenu} />
      ) : effectiveMode === "saas" ? (
        <SaasDashboard />
      ) : effectiveMode === "concrete_pumping" ? (
        <ConcretePumpingDashboard view={homeView} onViewChange={setHomeView} />
      ) : (
        <ConstructionDashboard mode={effectiveMode} view={homeView} onViewChange={setHomeView} />
      )}
    </div>
  );
}

// ── Concrete pumping dashboard ────────────────────────────────────────────────

type PumpingQueueItem = {
  label: string;
  detail: string;
  value: string | number;
  href: string;
  tone: "amber" | "blue" | "slate";
};

function ConcretePumpingDashboard({
  view,
  onViewChange,
}: {
  view: HomeView;
  onViewChange: (v: HomeView) => void;
}) {
  const simple = view !== "detailed";
  const projectsQ = useQuery(projectsListQueryOptions({ limit: 500 }));
  const ticketsQ = useQuery(tmTicketsListQueryOptions());
  const tmInvoicesQ = useQuery(tmInvoicesListQueryOptions());
  const equipmentQ = useQuery(equipmentItemsQueryOptions({ active_only: true }));
  const unpostedEquipmentQ = useQuery(
    equipmentUnpostedTimeEntriesQueryOptions({ limit: 200 }),
  );
  const arAgingQ = useQuery(arAgingQueryOptions());
  const approvalsQ = useQuery(
    approvalRequestsListQueryOptions({ state: "pending" }),
  );
  const intakeQ = useQuery(apIntakeListQueryOptions());
  const accountsQ = useQuery(bankAccountsListQueryOptions());

  const projects = projectsQ.data;
  const tickets = ticketsQ.data;
  const tmInvoices = tmInvoicesQ.data;
  const equipment = equipmentQ.data;
  const unpostedEquipment = unpostedEquipmentQ.data;
  const arAging = arAgingQ.data;
  const approvals = approvalsQ.data;
  const intake = intakeQ.data;
  const accounts = accountsQ.data;
  const queueLoading =
    ticketsQ.isPending ||
    unpostedEquipmentQ.isPending ||
    arAgingQ.isPending ||
    approvalsQ.isPending ||
    intakeQ.isPending;
  const queueError =
    ticketsQ.isError ||
    unpostedEquipmentQ.isError ||
    arAgingQ.isError ||
    approvalsQ.isError ||
    intakeQ.isError;
  const retryQueue = () => {
    void Promise.all([
      ticketsQ.refetch(),
      unpostedEquipmentQ.refetch(),
      arAgingQ.refetch(),
      approvalsQ.refetch(),
      intakeQ.refetch(),
    ]);
  };

  const activeProjects = (projects?.items ?? []).filter((p) => p.status === "active");
  const allTickets = tickets?.items ?? [];
  const eligibleTickets = allTickets.filter((t) => t.status === "eligible");
  const draftTickets = allTickets.filter((t) => t.status === "draft");
  const allTmInvoices = tmInvoices?.items ?? [];
  const invoiceWork = allTmInvoices.filter((i) => i.status === "draft" || i.status === "sent");
  const activeEquipment = equipment ?? [];
  const ownedEquipment = activeEquipment.filter((e) => e.ownership_type === "owned");
  const rentedEquipment = activeEquipment.filter((e) => e.ownership_type === "rented" || e.ownership_type === "leased");
  const unpostedCount = unpostedEquipment?.items.length ?? 0;
  const unpostedLabel = unpostedEquipment?.next_cursor ? `${unpostedCount}+` : unpostedCount;
  const billsToReview = openIntakeItems(intake?.items);
  const pendingApprovals = (approvals ?? []).filter((a) => a.state === "pending");
  const overdueAr = (arAging ?? []).filter((r) => r.days_past_due > 0);
  const overdueArTotal = overdueAr.reduce((sum, row) => sum + parseFloat(row.open_amount), 0);
  const activeAccounts = (accounts ?? []).filter((a) => a.is_active);
  const totalCash = activeAccounts.reduce(
    (sum, account) => sum + parseFloat(account.current_balance ?? "0"),
    0,
  );

  const queue: PumpingQueueItem[] = [
    ...(eligibleTickets.length > 0
      ? [{
          label: "Pour tickets ready to bill",
          detail: "Pricing is complete; build the customer invoice.",
          value: eligibleTickets.length,
          href: "/tm-invoices/new",
          tone: "amber" as const,
        }]
      : []),
    ...(draftTickets.length > 0
      ? [{
          label: "Draft pour tickets",
          detail: "Finish the field record and confirm billable work.",
          value: draftTickets.length,
          href: "/tm-tickets",
          tone: "blue" as const,
        }]
      : []),
    ...(unpostedCount > 0
      ? [{
          label: "Fleet activity not posted",
          detail: "Review pump, idle, and transport time before job costing.",
          value: unpostedLabel,
          href: "/equipment/cost-posting",
          tone: "amber" as const,
        }]
      : []),
    ...(billsToReview.length > 0
      ? [{
          label: "Fuel, repair, or vendor bills to review",
          detail: "Code the incoming cost to the right asset, job, and branch.",
          value: billsToReview.length,
          href: "/bills-intake",
          tone: "slate" as const,
        }]
      : []),
    ...(pendingApprovals.length > 0
      ? [{
          label: "Approvals waiting",
          detail: "Clear purchasing and payment decisions.",
          value: pendingApprovals.length,
          href: "/approvals",
          tone: "slate" as const,
        }]
      : []),
    ...(overdueAr.length > 0
      ? [{
          label: "Past-due customer balances",
          detail: `${overdueAr.length} invoice${overdueAr.length === 1 ? "" : "s"} need collection follow-up.`,
          value: fmtCompact(overdueArTotal),
          href: "/reports/ar-aging",
          tone: "amber" as const,
        }]
      : []),
  ];

  const askCfo = () =>
    window.dispatchEvent(new CustomEvent("bynaus:open-agent", {
      detail: {
        prompt: "Give me the concrete pumping tour: pour ticket to invoice, fleet costing, fuel and maintenance, branch reporting, and month-end.",
        autoSend: true,
      },
    }));

  return (
    <div className="overflow-y-auto flex-1 bg-[#f4f7f6] dark:bg-gray-950">
      <div className="mx-auto max-w-[1500px] px-4 sm:px-6 py-5 sm:py-7">
        {view === "minimal" && (
          <div className="flex justify-end mb-3">
            <HomeViewSwitcher view={view} onChange={onViewChange} />
          </div>
        )}

        <section className="relative overflow-hidden rounded-2xl bg-[#173e3a] text-white shadow-sm mb-5">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-300/10" />
          <div className="absolute right-32 -bottom-32 h-64 w-64 rounded-full bg-sky-300/10" />
          <div className="relative px-5 py-5 sm:px-7 sm:py-6 flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold tracking-wide text-emerald-100">
                  CONCRETE PUMPING OPERATIONS
                </span>
                <span className="text-xs text-emerald-100/70">Standalone ready</span>
              </div>
              <h2 className="text-2xl sm:text-[28px] leading-tight font-semibold tracking-tight">
                From pour ticket to cash, with the pump costs attached.
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-emerald-50/75 leading-6">
                Keep jobs, signed field work, crew time, fleet activity, billing, and collections in one clear operating flow across every branch.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <Link
                to="/tm-tickets/new"
                className="inline-flex items-center rounded-lg bg-[#f4b740] px-4 py-2.5 text-sm font-semibold text-[#17302d] no-underline shadow-sm hover:bg-[#ffc85d] transition-colors"
              >
                + New pour ticket
              </Link>
              <Link
                to="/tm-invoices/new"
                className="inline-flex items-center rounded-lg border border-white/25 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white no-underline hover:bg-white/15 transition-colors"
              >
                Build invoice
              </Link>
              <button
                type="button"
                onClick={askCfo}
                className="inline-flex items-center rounded-lg px-3 py-2.5 text-sm font-medium text-emerald-50/85 hover:bg-white/10 transition-colors"
              >
                Take the CFO tour →
              </button>
            </div>
          </div>
        </section>

        <section aria-label="Field-to-cash flow" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
          <PumpingFlowCard
            step="01"
            eyebrow="Jobs & sites"
            value={projectsQ.isPending ? "—" : activeProjects.length}
            detail="Active jobs ready for field activity"
            href="/projects"
            tone="green"
          />
          <PumpingFlowCard
            step="02"
            eyebrow="Pour tickets"
            value={ticketsQ.isPending ? "—" : eligibleTickets.length}
            detail={ticketsQ.isPending ? "Loading field tickets…" : `${draftTickets.length} draft · ${allTickets.length} total`}
            href="/tm-tickets"
            tone="blue"
          />
          <PumpingFlowCard
            step="03"
            eyebrow="Billing"
            value={tmInvoicesQ.isPending ? "—" : invoiceWork.length}
            detail={ticketsQ.isPending ? "Loading billable work…" : `${eligibleTickets.length} ticket${eligibleTickets.length === 1 ? "" : "s"} ready to invoice`}
            href="/tm-invoices"
            tone="gold"
          />
          <PumpingFlowCard
            step="04"
            eyebrow="Collections"
            value={arAgingQ.isPending ? "—" : overdueAr.length > 0 ? fmtCompact(overdueArTotal) : "$0"}
            detail={arAgingQ.isPending ? "Loading collections…" : `${overdueAr.length} past-due invoice${overdueAr.length === 1 ? "" : "s"}`}
            href="/reports/ar-aging"
            tone="slate"
          />
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-5 gap-5 mb-5">
          <div className="xl:col-span-3 rounded-2xl border border-slate-200/90 bg-white dark:bg-gray-900 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-slate-100 dark:border-gray-800">
              <div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Work queue</h3>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">The next decisions that move work toward cash</p>
              </div>
              <span className="rounded-full bg-slate-100 dark:bg-gray-800 px-2.5 py-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
                {queueLoading ? "—" : queue.length} item{queue.length === 1 ? "" : "s"}
              </span>
            </div>
            {queueLoading ? (
              <div className="px-5 py-8" role="status">
                <div className="h-3 w-36 rounded bg-slate-100 dark:bg-gray-800 animate-pulse" />
                <div className="mt-3 h-3 w-64 max-w-full rounded bg-slate-100 dark:bg-gray-800 animate-pulse" />
                <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">Loading operating work…</p>
              </div>
            ) : queueError ? (
              <div className="px-5 py-7">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Some operating data could not be loaded.</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">The rest of Ledger Core is still available.</p>
                <button type="button" onClick={retryQueue} className="mt-3 text-sm font-semibold text-emerald-700 dark:text-emerald-300 hover:underline">
                  Try again
                </button>
              </div>
            ) : queue.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300">✓</div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">The queue is clear.</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Start the next field record when a pour is complete.</p>
                <Link to="/tm-tickets/new" className="mt-4 inline-flex text-sm font-semibold text-emerald-700 dark:text-emerald-300 no-underline hover:underline">
                  Create a pour ticket →
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-gray-800">
                {queue.slice(0, simple ? 5 : 8).map((item) => (
                  <PumpingQueueRow key={item.label} item={item} />
                ))}
              </div>
            )}
          </div>

          <div className="xl:col-span-2 rounded-2xl border border-slate-200/90 bg-white dark:bg-gray-900 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-gray-800">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Fleet readiness</h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">One register for owned, leased, and rented equipment</p>
            </div>
            <div className="grid grid-cols-3 divide-x divide-slate-100 dark:divide-gray-800 border-b border-slate-100 dark:border-gray-800">
              <FleetMetric label="Active" value={equipmentQ.isPending ? "—" : activeEquipment.length} />
              <FleetMetric label="Owned" value={equipmentQ.isPending ? "—" : ownedEquipment.length} />
              <FleetMetric label="Rented" value={equipmentQ.isPending ? "—" : rentedEquipment.length} />
            </div>
            <div className="p-4 space-y-2">
              <PumpingTextLink href="/equipment" label="Open the pump fleet" detail="Rates, service history, PM schedules, fuel and repairs" />
              <PumpingTextLink href="/equipment/cost-posting" label="Post fleet activity" detail={`${unpostedLabel} unposted time entr${unpostedCount === 1 ? "y" : "ies"}`} />
              <PumpingTextLink href="/equipment/reports/variance" label="Review operating variance" detail="Recovery compared with actual fuel and maintenance" />
            </div>
            <div className="mx-4 mb-4 rounded-xl border border-dashed border-sky-200 dark:border-sky-900 bg-sky-50/70 dark:bg-sky-950/25 px-4 py-3">
              <p className="text-xs font-semibold text-sky-900 dark:text-sky-200">Connecting dispatch, telematics, maintenance, or fuel?</p>
              <p className="mt-1 text-xs leading-5 text-sky-800/75 dark:text-sky-300/75">Start with the system that owns the completed signed ticket. Ledger Core can run standalone while connector mapping is confirmed.</p>
              <Link to={"/help/docs/concrete-pumping/integrations.md" as any} className="mt-2 inline-flex text-xs font-semibold text-sky-800 dark:text-sky-300 no-underline hover:underline">
                Open the integration guide →
              </Link>
            </div>
          </div>
        </section>

        {!simple && (
          <>
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
              <div className="lg:col-span-2">
                <div className="flex items-end justify-between gap-4 mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Know where the margin went</h3>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Four views for job, branch, and fleet performance</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <PumpingReportCard title="Job profitability" detail="Budget, actual, committed, forecast, and margin" href="/job-cost" />
                  <PumpingReportCard title="Unbilled work & WIP" detail="Find completed work not yet reflected in billing" href="/reports/wip" />
                  <PumpingReportCard title="Fleet cost by job" detail="Pump and equipment recovery by job and cost code" href="/equipment/reports/cost-by-job" />
                  <PumpingReportCard title="Fuel & maintenance variance" detail="Compare operating recovery with actual costs" href="/equipment/reports/variance" />
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200/90 bg-white dark:bg-gray-900 dark:border-gray-800 shadow-sm p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Cash position</h3>
                    <p className="mt-1 text-3xl font-semibold tracking-tight text-[#173e3a] dark:text-emerald-300">
                      {accounts ? fmtCompact(totalCash) : "—"}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Across {activeAccounts.length} active bank account{activeAccounts.length === 1 ? "" : "s"}</p>
                  </div>
                  <span className="rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2 py-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">LEDGER</span>
                </div>
                <div className="mt-5 space-y-2 border-t border-slate-100 dark:border-gray-800 pt-4">
                  <PumpingTextLink href="/bank-accounts" label="Review cash accounts" detail="Balances, loans, and connected feeds" />
                  <PumpingTextLink href="/cash-flow" label="Open cash flow" detail="Statement and forward cash view" />
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200/90 bg-white dark:bg-gray-900 dark:border-gray-800 shadow-sm px-5 py-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="lg:w-56 flex-shrink-0">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Month-end route</h3>
                  <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">Close in the same order the work happens.</p>
                </div>
                <div className="grid flex-1 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                  <CloseStep number="1" label="Reconcile tickets" href="/tm-tickets" />
                  <CloseStep number="2" label="Post pump costs" href="/equipment/cost-posting" />
                  <CloseStep number="3" label="Review WIP & AR" href="/reports/wip" />
                  <CloseStep number="4" label="Close the period" href="/close" />
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function PumpingFlowCard({
  step,
  eyebrow,
  value,
  detail,
  href,
  tone,
}: {
  step: string;
  eyebrow: string;
  value: string | number;
  detail: string;
  href: string;
  tone: "green" | "blue" | "gold" | "slate";
}) {
  const tones = {
    green: "border-emerald-200/80 dark:border-emerald-900/60 bg-emerald-50/55 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-300",
    blue: "border-sky-200/80 dark:border-sky-900/60 bg-sky-50/55 dark:bg-sky-950/20 text-sky-800 dark:text-sky-300",
    gold: "border-amber-200/80 dark:border-amber-900/60 bg-amber-50/55 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300",
    slate: "border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-slate-700 dark:text-slate-300",
  };
  return (
    <Link to={href as any} className="no-underline group">
      <div className={`h-full rounded-xl border px-4 py-4 shadow-sm transition-all group-hover:-translate-y-0.5 group-hover:shadow-md ${tones[tone]}`}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] font-bold tracking-[0.18em] opacity-55">{step}</span>
          <span className="text-sm opacity-45 transition-transform group-hover:translate-x-0.5">→</span>
        </div>
        <p className="mt-2 text-xs font-semibold uppercase tracking-wide opacity-70">{eyebrow}</p>
        <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">{value}</p>
        <p className="mt-1 text-xs leading-5 opacity-75">{detail}</p>
      </div>
    </Link>
  );
}

function PumpingQueueRow({ item }: { item: PumpingQueueItem }) {
  const dot = item.tone === "amber" ? "bg-amber-500" : item.tone === "blue" ? "bg-sky-500" : "bg-slate-400";
  return (
    <Link to={item.href as any} className="group flex items-center gap-3 px-5 py-3.5 no-underline hover:bg-slate-50/80 dark:hover:bg-gray-800/60 transition-colors">
      <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${dot}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{item.label}</p>
        <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{item.detail}</p>
      </div>
      <span className="rounded-lg bg-slate-100 dark:bg-gray-800 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200">{item.value}</span>
      <span className="text-slate-300 dark:text-slate-600 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors">→</span>
    </Link>
  );
}

function FleetMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="px-3 py-4 text-center">
      <p className="text-xl font-semibold text-slate-900 dark:text-slate-100">{value}</p>
      <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</p>
    </div>
  );
}

function PumpingTextLink({ href, label, detail }: { href: string; label: string; detail: string }) {
  return (
    <Link to={href as any} className="group flex items-start gap-3 rounded-lg px-2 py-2 no-underline hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors">
      <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-slate-100 dark:bg-gray-800 text-xs text-slate-500 dark:text-slate-300">→</span>
      <span className="min-w-0">
        <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100 group-hover:text-emerald-800 dark:group-hover:text-emerald-300">{label}</span>
        <span className="mt-0.5 block text-[11px] leading-4 text-slate-500 dark:text-slate-400">{detail}</span>
      </span>
    </Link>
  );
}

function PumpingReportCard({ title, detail, href }: { title: string; detail: string; href: string }) {
  return (
    <Link to={href as any} className="group rounded-xl border border-slate-200/90 bg-white dark:bg-gray-900 dark:border-gray-800 px-4 py-4 no-underline shadow-sm hover:border-emerald-300 dark:hover:border-emerald-800 hover:shadow-md transition-all">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</p>
          <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{detail}</p>
        </div>
        <span className="text-slate-300 dark:text-slate-600 group-hover:translate-x-0.5 group-hover:text-emerald-700 dark:group-hover:text-emerald-300 transition-all">→</span>
      </div>
    </Link>
  );
}

function CloseStep({ number, label, href }: { number: string; label: string; href: string }) {
  return (
    <Link to={href as any} className="group flex items-center gap-2.5 rounded-lg bg-slate-50 dark:bg-gray-800/70 px-3 py-2.5 no-underline hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors">
      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white dark:bg-gray-900 text-[10px] font-bold text-slate-500 dark:text-slate-300 shadow-sm">{number}</span>
      <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 group-hover:text-emerald-800 dark:group-hover:text-emerald-300">{label}</span>
    </Link>
  );
}

// ── ConstructionDashboard ─────────────────────────────────────────────────────

function ConstructionDashboard({
  mode,
  view,
  onViewChange,
}: {
  mode: string;
  view: HomeView;
  onViewChange: (v: HomeView) => void;
}) {
  // Minimal shows the same stripped content as Simple — it just also drops
  // the page-header row (handled in HomePage).
  const simple = view !== "detailed";
  const modules = mode === "hospitality" ? HOSPITALITY_MODULES : MODULES;
  const { data: periods }   = useQuery(periodsListQueryOptions());
  const { data: apAging }   = useQuery(apAgingQueryOptions());
  const { data: arAging }   = useQuery(arAgingQueryOptions());
  const { data: approvals } = useQuery(approvalRequestsListQueryOptions({ state: "pending" }));
  const { data: projects }  = useQuery(projectsListQueryOptions());
  const { data: cpAlerts }  = useQuery(certifiedPayrollAlertsQueryOptions());
  const { data: overdraft } = useQuery(overdraftAlertsQueryOptions());
  const { data: accounts }  = useQuery(bankAccountsListQueryOptions());
  const { data: wip }       = useQuery(wipQueryOptions());
  // Fetch extra rows because routine login updates are omitted below.
  const { data: auditPage } = useQuery(auditLogQueryOptions({ limit: 50 }));
  const { data: payRuns }   = useQuery(payRunsQueryOptions());
  const { data: intake }    = useQuery(apIntakeListQueryOptions());

  // ── Derived numbers ──────────────────────────────────────────────────────
  const openPeriod = periods?.find((p) => p.status === "open");

  const apOverdue      = (apAging ?? []).filter((r) => r.days_past_due > 0);
  const apOverdueTotal = apOverdue.reduce((s, r) => s + parseFloat(r.open_amount), 0);

  const arOverdue      = (arAging ?? []).filter((r) => r.days_past_due > 0);
  const arOverdueTotal = arOverdue.reduce((s, r) => s + parseFloat(r.open_amount), 0);

  const pendingApprovals = (approvals ?? []).filter((a) => a.state === "pending");
  const activeProjects   = (projects?.items ?? []).filter((p) => p.status === "active");
  // Projects are a construction-pack concept — only in construction mode.
  const constructionEnabled = mode === "construction";

  const activeAccounts = (accounts ?? []).filter((a) => a.is_active);
  const totalCash = activeAccounts.reduce((s, a) => s + parseFloat(a.current_balance ?? "0"), 0);

  const wipRows: WipRow[] = [...(wip ?? [])]
    .sort((a, b) => Math.abs(parseFloat(b.over_under_billing)) - Math.abs(parseFloat(a.over_under_billing)))
    .slice(0, 6);

  const openPayRuns: PayRun[] = (payRuns ?? [])
    .filter((r) => !["posted", "voided"].includes(r.status))
    .slice(0, 5);

  // Bills waiting in intake — the "a new bill arrived by email" signal.
  const openIntake = openIntakeItems(intake?.items);
  const intakeCount = openIntake.length;
  const newestIntakeLabel = (() => {
    const r = openIntake[0];
    if (!r) return "";
    return (
      r.parsed_payload?.vendor_name ||
      r.source_metadata?.subject ||
      r.source_file_original_name ||
      "unidentified bill"
    );
  })();

  // ── Action items ─────────────────────────────────────────────────────────
  type ActionItem = { id: string; label: string; sub: string; href: string; urgent: boolean };
  const actions: ActionItem[] = [];

  if (intakeCount > 0) {
    actions.push({
      id: "bills-intake",
      label: `${intakeCount} bill${intakeCount > 1 ? "s" : ""} waiting in intake`,
      sub: `Latest: ${newestIntakeLabel}${openIntake[0] ? ` · ${timeAgo(openIntake[0].created_at)}` : ""}`,
      href: "/bills-intake",
      urgent: true,
    });
  }
  if (pendingApprovals.length > 0) {
    actions.push({
      id: "approvals",
      label: `${pendingApprovals.length} pending approval${pendingApprovals.length > 1 ? "s" : ""}`,
      sub: pendingApprovals
        .slice(0, 3)
        .map((a) => `${a.source_type}${a.amount ? ` · ${fmt(parseFloat(a.amount))}` : ""}`)
        .join("  ·  "),
      href: "/approvals",
      urgent: true,
    });
  }
  if (apOverdue.length > 0) {
    actions.push({
      id: "ap-overdue",
      label: `${apOverdue.length} overdue AP invoice${apOverdue.length > 1 ? "s" : ""}`,
      sub: fmt(apOverdueTotal) + " total past due",
      href: "/ap",
      urgent: apOverdueTotal > 10_000,
    });
  }
  if (arOverdue.length > 0) {
    actions.push({
      id: "ar-overdue",
      label: `${arOverdue.length} overdue AR invoice${arOverdue.length > 1 ? "s" : ""}`,
      sub: fmt(arOverdueTotal) + " total past due",
      href: "/ar",
      urgent: false,
    });
  }

  // Overdraft alerts — charged fees + upcoming auto-draft overdraw risk
  for (const [i, alert] of (overdraft?.alerts ?? []).entries()) {
    actions.push({
      id: `overdraft-${alert.type}-${i}`,
      label: alert.title,
      sub: alert.message,
      href: "/bank-accounts",
      urgent: alert.severity === "critical",
    });
  }

  // Certified payroll alerts — wh347_due and pw_violations
  for (const alert of cpAlerts ?? []) {
    actions.push({
      id: `cp-alert-${alert.pay_run_id}`,
      label: alert.message,
      sub: alert.project_code ? `Project ${alert.project_code}` : "",
      href: alert.action_url,
      urgent: alert.severity === "urgent",
    });
  }

  // Simple view hides the Open pay runs card, so an open run surfaces here
  // instead. Detailed view keeps the card and skips this row (no duplicate).
  if (simple && openPayRuns.length > 0) {
    const run = openPayRuns[0];
    actions.push({
      id: "open-pay-run",
      label:
        openPayRuns.length === 1
          ? `Pay run ${run.pay_run_number} is ${PAY_RUN_STATUS_LABEL[run.status]?.toLowerCase() ?? run.status}`
          : `${openPayRuns.length} open pay runs`,
      sub: `${fmtDate(run.period_start)} → ${fmtDate(run.period_end)} · Pay ${fmtDate(run.pay_date)}`,
      href: "/payroll/pay-runs",
      urgent: false,
    });
  }

  return (
    <div className="overflow-y-auto flex-1 px-6 py-6 bg-slate-50 dark:bg-gray-950">

      {/* ── Open period warning (construction) ── */}
      {!openPeriod && (
        <div className="mb-4 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400 font-medium">
          Your books aren't open yet — <a href="/periods" className="underline font-semibold">open your first accounting period</a> to start posting transactions.
        </div>
      )}

      {/* ── Overdraft alerts (charged fees + auto-draft overdraw risk) ── */}
      {(overdraft?.alerts ?? []).map((a, i) => {
        const critical = a.severity === "critical";
        return (
          <div
            key={`od-banner-${i}`}
            className={
              "mb-4 px-4 py-3 rounded-lg border " +
              (critical
                ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"
                : "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800")
            }
          >
            <div className="flex items-start gap-2">
              {critical
                ? <ErrorOutlineIcon className="text-[20px] text-red-600 dark:text-red-400" />
                : <WarningAmberOutlinedIcon className="text-[20px] text-amber-600 dark:text-amber-400" />}
              <div className="flex-1 min-w-0">
                <div
                  className={
                    "text-sm font-semibold " +
                    (critical
                      ? "text-red-800 dark:text-red-300"
                      : "text-amber-800 dark:text-amber-300")
                  }
                >
                  {a.title}
                </div>
                <p className="text-sm mt-0.5 text-slate-700 dark:text-slate-300">{a.message}</p>
                {a.advice?.length > 0 && (
                  <ul className="mt-1.5 ml-1 list-disc list-inside space-y-0.5 text-xs text-slate-600 dark:text-slate-400">
                    {a.advice.slice(0, 3).map((adv, j) => (
                      <li key={j}>{adv}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* ── Casino floor metrics (hospitality view only) — lead with these ── */}
      {mode === "hospitality" && <HospitalityDashboardMetrics />}

      {/* Minimal view: the header row is gone, so the view switcher lives
          here — small, right-aligned, above the KPIs. */}
      {view === "minimal" && (
        <div className="flex justify-end mb-3">
          <HomeViewSwitcher view={view} onChange={onViewChange} />
        </div>
      )}

      {/* ── KPI tiles ──
          Simple: the four that demand action. Detailed: the full set plus
          the new Bills to review tile (new bills must be visible in both). */}
      <div className={`grid grid-cols-2 ${simple ? "sm:grid-cols-4" : constructionEnabled ? "sm:grid-cols-6" : "sm:grid-cols-5"} gap-4 mb-6`}>
        <StatCard
          label="Total Cash"
          value={accounts ? fmtCompact(totalCash) : "—"}
          sub={accounts ? `${activeAccounts.length} account${activeAccounts.length !== 1 ? "s" : ""}` : undefined}
          href="/bank-accounts"
          urgent={false}
          accent="blue"
        />
        <StatCard
          label="Bills to review"
          value={intakeCount}
          sub={intakeCount > 0 ? `newest ${timeAgo(openIntake[0].created_at)}` : undefined}
          href="/bills-intake"
          urgent={intakeCount > 0}
        />
        <StatCard
          label="Pending Approvals"
          value={pendingApprovals.length}
          href="/approvals"
          urgent={pendingApprovals.length > 0}
        />
        <StatCard
          label="AP Overdue"
          value={apOverdue.length > 0 ? fmtCompact(apOverdueTotal) : "—"}
          sub={apOverdue.length > 0 ? `${apOverdue.length} invoice${apOverdue.length > 1 ? "s" : ""}` : undefined}
          href="/ap"
          urgent={apOverdueTotal > 0}
        />
        {!simple && (
          <StatCard
            label="AR Overdue"
            value={arOverdue.length > 0 ? fmtCompact(arOverdueTotal) : "—"}
            sub={arOverdue.length > 0 ? `${arOverdue.length} invoice${arOverdue.length > 1 ? "s" : ""}` : undefined}
            href="/ar"
            urgent={false}
          />
        )}
        {!simple && constructionEnabled && (
          <StatCard
            label="Active Projects"
            value={activeProjects.length || (projects?.total ?? 0)}
            href="/projects"
            urgent={false}
          />
        )}
      </div>


      {/* ── Row 2 ──
          Simple:   Needs attention (hero, 2/3) · Recent activity (1/3)
          Detailed: Needs attention · Cash position · Open pay runs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

        {/* Needs attention */}
        <div className={simple ? "lg:col-span-2" : undefined}>
          <SectionHeading>Needs attention</SectionHeading>
          <Card>
            {actions.length === 0 ? (
              <p className="px-4 py-5 text-sm text-slate-400 dark:text-slate-500">
                Nothing urgent right now.
              </p>
            ) : (
              actions.map((a) => (
                <Link
                  key={a.id}
                  to={a.href as any}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors no-underline group"
                >
                  <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${a.urgent ? "bg-amber-500" : "bg-slate-300 dark:bg-slate-600"}`} />
                  <div className="min-w-0">
                    <p className={`text-sm font-medium ${a.urgent ? "text-slate-900 dark:text-slate-100" : "text-slate-700 dark:text-slate-300"}`}>
                      {a.label}
                    </p>
                    {a.sub && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5">{a.sub}</p>
                    )}
                  </div>
                  <span className="ml-auto text-slate-300 dark:text-slate-600 group-hover:text-slate-500 text-xs flex-shrink-0 mt-0.5">→</span>
                </Link>
              ))
            )}
          </Card>
        </div>

        {/* Recent activity — Simple view puts it beside Needs attention */}
        {simple && (
          <RecentActivitySection auditRows={auditPage?.rows} limit={10} />
        )}

        {/* Cash position (Detailed only — Total Cash tile covers Simple) */}
        {!simple && (
        <div>
          <SectionHeading href="/bank-accounts">Cash position</SectionHeading>
          <Card>
            {activeAccounts.length === 0 ? (
              <p className="px-4 py-5 text-sm text-slate-400 dark:text-slate-500">
                No bank accounts configured.
              </p>
            ) : (
              <>
                {activeAccounts.slice(0, 5).map((acct) => (
                  <BankAccountRow key={acct.id} account={acct} />
                ))}
                {activeAccounts.length > 1 && (
                  <div className="px-4 py-2.5 border-t border-slate-100 dark:border-gray-800 flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total</span>
                    <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{fmt(totalCash)}</span>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
        )}

        {/* Open pay runs (Detailed only — Simple folds an open run into Needs attention) */}
        {!simple && (
        <div>
          <SectionHeading href="/payroll/pay-runs">Open pay runs</SectionHeading>
          <Card>
            {openPayRuns.length === 0 ? (
              <p className="px-4 py-5 text-sm text-slate-400 dark:text-slate-500">
                No open pay runs.
              </p>
            ) : (
              openPayRuns.map((run) => (
                <Link
                  key={run.id}
                  to="/payroll/pay-runs"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors no-underline group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {run.pay_run_number}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                      {fmtDate(run.period_start)} → {fmtDate(run.period_end)} · Pay {fmtDate(run.pay_date)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${PAY_RUN_STATUS_COLOR[run.status] ?? PAY_RUN_STATUS_COLOR.draft}`}>
                      {PAY_RUN_STATUS_LABEL[run.status] ?? run.status}
                    </span>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                      {fmtCompact(parseFloat(run.total_gross))}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </Card>
        </div>
        )}

      </div>

      {/* ── WIP snapshot (Detailed only) ── */}
      {!simple && wipRows.length > 0 && (
        <div className="mb-6">
          <SectionHeading href="/reports">WIP / Job cost snapshot</SectionHeading>
          <div className="bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-800 shadow-sm dark:shadow-none rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-gray-800">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Project</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Contract</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Cost to date</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide hidden sm:table-cell">Billed</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Over / (Under)</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">% Complete</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-gray-800">
                {wipRows.map((row) => {
                  const overUnder = parseFloat(row.over_under_billing);
                  const pct       = parseFloat(row.pct_complete);
                  return (
                    <tr key={row.project_id} className="hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-3">
                        <Link to="/projects" className="font-medium text-slate-900 dark:text-slate-100 hover:text-blue-600 dark:hover:text-blue-400 no-underline truncate block max-w-[180px]">
                          {row.project_name}
                        </Link>
                        <span className="text-xs text-slate-400 dark:text-slate-500">{row.project_code}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">
                        {fmtCompact(parseFloat(row.revised_contract))}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">
                        {fmtCompact(parseFloat(row.cost_incurred))}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums hidden sm:table-cell">
                        {fmtCompact(parseFloat(row.billed_to_date))}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className={overUnder > 0 ? "text-red-600 dark:text-red-400 font-medium" : overUnder < 0 ? "text-green-600 dark:text-green-400" : "text-slate-400"}>
                          {overUnder > 0 ? "+" : ""}{fmtCompact(overUnder)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-slate-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-blue-500"
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-400 tabular-nums w-8 text-right">
                            {pct.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(wip?.length ?? 0) > 6 && (
              <div className="px-4 py-2.5 border-t border-slate-100 dark:border-gray-800 text-center">
                <Link to="/reports" className="text-xs text-blue-600 dark:text-blue-400 hover:underline no-underline">
                  View all {wip!.length} projects →
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Bottom row: modules + recent activity (Detailed only —
          the Modules grid duplicates the top menu, and Simple shows
          Recent activity beside Needs attention above) ── */}
      {!simple && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Module quick links */}
        <div className="lg:col-span-2">
          <SectionHeading>Modules</SectionHeading>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {modules.map((m) => (
              <Link key={m.label} to={m.href as any} className="no-underline">
                <div className="group bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-800 shadow-sm dark:shadow-none rounded-lg px-3 py-3 flex flex-col items-center gap-1.5 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors cursor-pointer">
                  <m.icon className="text-[22px] text-slate-400 dark:text-slate-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400 text-center leading-tight">{m.label}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <RecentActivitySection auditRows={auditPage?.rows} limit={10} />

      </div>
      )}

    </div>
  );
}

// ── SaasDashboard ─────────────────────────────────────────────────────────────

function SaasDashboard() {
  const { data: mrrData }      = useQuery(saasMrrQueryOptions());
  const { data: arrData }      = useQuery(saasArrQueryOptions());
  const { data: subs }         = useQuery(saasSubscriptionsQueryOptions());
  const { data: snapshots }    = useQuery(saasSnapshotsQueryOptions(1));
  const { data: schedules }    = useQuery(saasRecognitionSchedulesQueryOptions());
  const { data: accounts }     = useQuery(bankAccountsListQueryOptions());
  // Fetch extra rows because routine login updates are omitted below.
  const { data: auditPage }    = useQuery(auditLogQueryOptions({ limit: 50 }));
  const { data: periods }      = useQuery(periodsListQueryOptions());
  const { data: arAging }      = useQuery(arAgingQueryOptions());
  const { data: burnData }     = useQuery(saasBurnQueryOptions(12));
  const ltvDates = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setFullYear(start.getFullYear() - 1);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  }, []);
  const { data: ltvCacData } = useQuery(saasLtvCacQueryOptions(ltvDates.start, ltvDates.end));

  const snapshot: MetricsSnapshot | undefined = snapshots?.[0];
  const allSubs: Subscription[] = subs ?? [];
  const currencyCode = snapshot?.base_currency_code ?? "USD";

  const activeSubs  = allSubs.filter((s) => s.status === "active");
  const pastDueSubs = allSubs.filter((s) => s.status === "past_due");
  const trialSubs   = allSubs.filter((s) => s.status === "trialing");

  const activeAccounts = (accounts ?? []).filter((a) => a.is_active);
  const totalCash = activeAccounts.reduce((s, a) => s + parseFloat(a.current_balance ?? "0"), 0);

  const openPeriod = periods?.find((p) => p.status === "open");

  const arOverdue      = (arAging ?? []).filter((r) => r.days_past_due > 0);
  const arOverdueTotal = arOverdue.reduce((s, r) => s + parseFloat(r.open_amount), 0);

  // Upcoming renewals: active subs with next_invoice_date within 7 days
  const now = new Date();
  const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const upcomingRenewals = activeSubs.filter((s) => {
    if (!s.next_invoice_date) return false;
    const d = new Date(s.next_invoice_date);
    return d >= now && d <= in7Days;
  });

  // Action items for SaaS
  type ActionItem = { id: string; label: string; sub: string; href: string; urgent: boolean };
  const actions: ActionItem[] = [];

  if (pastDueSubs.length > 0) {
    actions.push({
      id: "past-due-subs",
      label: `${pastDueSubs.length} past-due subscription${pastDueSubs.length > 1 ? "s" : ""}`,
      sub: pastDueSubs.slice(0, 3).map((s) => s.subscription_no).join("  ·  "),
      href: "/saas",
      urgent: true,
    });
  }
  if (upcomingRenewals.length > 0) {
    actions.push({
      id: "renewals",
      label: `${upcomingRenewals.length} renewal${upcomingRenewals.length > 1 ? "s" : ""} due within 7 days`,
      sub: upcomingRenewals.slice(0, 3).map((s) => s.subscription_no).join("  ·  "),
      href: "/saas",
      urgent: false,
    });
  }
  if (arOverdue.length > 0) {
    actions.push({
      id: "ar-overdue",
      label: `${arOverdue.length} overdue AR invoice${arOverdue.length > 1 ? "s" : ""}`,
      sub: fmt(arOverdueTotal) + " total past due",
      href: "/ar",
      urgent: false,
    });
  }

  // Subscription pipeline: 6 most recently started
  const recentSubs = [...allSubs]
    .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())
    .slice(0, 6);

  // Recognition schedules — group by service_start month, cap to 6 rows
  const allSchedules: RecognitionSchedule[] = schedules ?? [];
  const scheduleRows = allSchedules.slice(0, 6);

  return (
    <div className="overflow-y-auto flex-1 px-6 py-6 bg-slate-50 dark:bg-gray-950">

      {/* ── Open period warning ── */}
      {!openPeriod && (
        <div className="mb-4 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400 font-medium">
          Your books aren't open yet — <a href="/periods" className="underline font-semibold">open your first accounting period</a> to start posting transactions.
        </div>
      )}

      {/* ── KPI tiles ──
          Grouped left-to-right: revenue (MRR/ARR) → efficiency
          (Burn, LTV/CAC) → subscription pipeline (Active/Past-due/Trialing)
          → external (API spend). Wraps to 2 rows of 4 on tablet, 8 across
          on wide desktop. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-4 mb-6">
        <StatCard
          label="MRR"
          value={mrrData ? `${Number(mrrData.mrr).toLocaleString("en-US", { style: "currency", currency: currencyCode, minimumFractionDigits: 2 })}` : "—"}
          href="/saas/metrics"
          urgent={false}
          accent="blue"
        />
        <StatCard
          label="ARR"
          value={arrData ? `${Number(arrData.arr).toLocaleString("en-US", { style: "currency", currency: currencyCode, minimumFractionDigits: 2 })}` : "—"}
          href="/saas/metrics"
          urgent={false}
          accent="blue"
        />
        <BurnRunwayTile burnData={burnData} />
        <LtvCacTile ltvCacData={ltvCacData} />
        <StatCard
          label="Active Subs"
          value={activeSubs.length}
          href="/saas"
          urgent={false}
        />
        <StatCard
          label="Past-due"
          value={pastDueSubs.length}
          href="/saas"
          urgent={pastDueSubs.length > 0}
        />
        <StatCard
          label="Trialing"
          value={trialSubs.length}
          href="/saas"
          urgent={false}
        />
        <VendorSpendTile />
      </div>

      {/* ── Row 2: Needs attention · Subscription pipeline · Cash position ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

        {/* Needs attention */}
        <div>
          <SectionHeading>Needs attention</SectionHeading>
          <Card>
            {actions.length === 0 ? (
              <p className="px-4 py-5 text-sm text-slate-400 dark:text-slate-500">
                Nothing urgent right now.
              </p>
            ) : (
              actions.map((a) => (
                <Link
                  key={a.id}
                  to={a.href as any}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors no-underline group"
                >
                  <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${a.urgent ? "bg-amber-500" : "bg-slate-300 dark:bg-slate-600"}`} />
                  <div className="min-w-0">
                    <p className={`text-sm font-medium ${a.urgent ? "text-slate-900 dark:text-slate-100" : "text-slate-700 dark:text-slate-300"}`}>
                      {a.label}
                    </p>
                    {a.sub && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5">{a.sub}</p>
                    )}
                  </div>
                  <span className="ml-auto text-slate-300 dark:text-slate-600 group-hover:text-slate-500 text-xs flex-shrink-0 mt-0.5">→</span>
                </Link>
              ))
            )}
          </Card>
        </div>

        {/* Subscription pipeline */}
        <div>
          <SectionHeading href="/saas">Subscription pipeline</SectionHeading>
          <Card>
            {recentSubs.length === 0 ? (
              <p className="px-4 py-5 text-sm text-slate-400 dark:text-slate-500">
                No subscriptions yet.
              </p>
            ) : (
              recentSubs.map((sub) => (
                <Link
                  key={sub.id}
                  to="/saas"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors no-underline group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {sub.subscription_no}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                      {sub.customer_id.slice(0, 8)}
                      {sub.items.length > 1 ? ` · ${sub.items.length} plans` : sub.items.length === 1 ? " · 1 plan" : ""}
                      {" · "}{fmtDate(sub.started_at)}
                    </p>
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${subStatusChipClass(sub.status)}`}>
                    {sub.status.replace(/_/g, " ")}
                  </span>
                </Link>
              ))
            )}
          </Card>
        </div>

        {/* Cash position */}
        <div>
          <SectionHeading href="/bank-accounts">Cash position</SectionHeading>
          <Card>
            {activeAccounts.length === 0 ? (
              <p className="px-4 py-5 text-sm text-slate-400 dark:text-slate-500">
                No bank accounts configured.
              </p>
            ) : (
              <>
                {activeAccounts.slice(0, 5).map((acct) => (
                  <BankAccountRow key={acct.id} account={acct} />
                ))}
                {activeAccounts.length > 1 && (
                  <div className="px-4 py-2.5 border-t border-slate-100 dark:border-gray-800 flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total</span>
                    <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{fmt(totalCash)}</span>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

      </div>

      {/* ── Row 3: Revenue recognition · Recent activity ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

        {/* Revenue recognition */}
        <div className="lg:col-span-2">
          <SectionHeading href="/recognition">Revenue recognition</SectionHeading>
          <div className="bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-800 shadow-sm dark:shadow-none rounded-lg overflow-hidden">
            {scheduleRows.length === 0 ? (
              <p className="px-4 py-5 text-sm text-slate-400 dark:text-slate-500">
                No recognition schedules found.
              </p>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-gray-800">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Service window</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide hidden sm:table-cell">Recognized</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Deferred</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide hidden md:table-cell">Progress</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 dark:divide-gray-800">
                    {scheduleRows.map((sched) => {
                      const total = parseFloat(sched.total_amount);
                      const recognized = sched.entries
                        .filter((e) => e.status === "posted")
                        .reduce((s, e) => s + parseFloat(e.amount), 0);
                      const deferred = total - recognized;
                      const pct = total > 0 ? Math.min((recognized / total) * 100, 100) : 0;
                      return (
                        <tr key={sched.id} className="hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {fmtDate(sched.service_start)} → {fmtDate(sched.service_end)}
                            </p>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                              {sched.period_count} period{sched.period_count !== 1 ? "s" : ""}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 tabular-nums">
                            {fmtCompact(total)}
                          </td>
                          <td className="px-4 py-3 text-right text-green-600 dark:text-green-400 tabular-nums hidden sm:table-cell">
                            {fmtCompact(recognized)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-500 dark:text-slate-400 tabular-nums">
                            {fmtCompact(deferred)}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-slate-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-green-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 tabular-nums w-8 text-right">
                                {pct.toFixed(0)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {allSchedules.length > 6 && (
                  <div className="px-4 py-2.5 border-t border-slate-100 dark:border-gray-800 text-center">
                    <Link to="/recognition" className="text-xs text-blue-600 dark:text-blue-400 hover:underline no-underline">
                      View all {allSchedules.length} schedules →
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <RecentActivitySection auditRows={auditPage?.rows} limit={8} />

      </div>

      {/* ── SaaS module quick links ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionHeading>Modules</SectionHeading>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {SAAS_MODULES.map((m) => (
              <Link key={m.label} to={m.href as any} className="no-underline">
                <div className="group bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-800 shadow-sm dark:shadow-none rounded-lg px-3 py-3 flex flex-col items-center gap-1.5 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors cursor-pointer">
                  <m.icon className="text-[22px] text-slate-400 dark:text-slate-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400 text-center leading-tight">{m.label}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function BurnRunwayTile({ burnData }: { burnData: any }) {
  // Burn is presented signed: positive cents = cash burning. We absolute it
  // for display but color red (burning) vs green (cash positive).
  const headline = burnData?.headline;
  if (!headline || headline.net_burn_cents === null) {
    return (
      <StatCard
        label="Net Burn / Runway"
        value="—"
        sub="Take a snapshot"
        href="/saas/metrics#burn"
        urgent={false}
      />
    );
  }
  const burning = headline.net_burn_cents > 0;
  const value = `$${Math.round(Math.abs(headline.net_burn_cents) / 100).toLocaleString()}/mo`;
  const sub = headline.runway_months !== null
    ? `${headline.runway_months.toFixed(1)} mo runway`
    : "Cash positive";
  // Mark urgent when runway is short — under 6 months is the "raise or cut" zone.
  const urgent = burning && headline.runway_months !== null && headline.runway_months < 6;
  return (
    <StatCard
      label="Net Burn / Runway"
      value={value}
      sub={sub}
      href="/saas/metrics#burn"
      urgent={urgent}
      accent={!burning ? "blue" : undefined}
    />
  );
}

function LtvCacTile({ ltvCacData }: { ltvCacData: any }) {
  // LTV/CAC = unit economics health check. Industry rule of thumb:
  // <1× burning capital, 1-3× okay-ish, 3×+ healthy.
  if (!ltvCacData || ltvCacData.ltv_cac_ratio <= 0) {
    return (
      <StatCard
        label="LTV / CAC"
        value="—"
        sub="Tag GL with saas_category"
        href="/saas/metrics#ltv-cac"
        urgent={false}
      />
    );
  }
  const ratio = ltvCacData.ltv_cac_ratio;
  const payback = ltvCacData.cac_payback_months;
  const value = `${ratio.toFixed(1)}×`;
  const sub = payback > 0
    ? `${payback.toFixed(1)} mo payback`
    : "—";
  // <1× is the bad zone — you're losing money per acquired customer.
  const urgent = ratio < 1;
  return (
    <StatCard
      label="LTV / CAC"
      value={value}
      sub={sub}
      href="/saas/metrics#ltv-cac"
      urgent={urgent}
      accent={ratio >= 3 ? "blue" : undefined}
    />
  );
}

function VendorSpendTile() {
  const q = useQuery({
    queryKey: ["vendor-watch", "accounts", "tile"],
    queryFn: () => api<{ accounts: any[]; count: number }>("/api/vendor-watch/accounts"),
    refetchInterval: 60_000,
  });
  const accounts = q.data?.accounts ?? [];
  const totalCents = accounts.reduce(
    (s, a) => s + (a.latest_snapshot?.amount_cents ?? 0), 0,
  );
  const critical = accounts.filter((a) =>
    a.is_mission_critical && a.active_alerts.some((al: any) => al.severity === "critical"),
  ).length;
  const anyAlerts = accounts.some((a) => a.active_alerts.length > 0);
  const value =
    accounts.length === 0
      ? "—"
      : `$${(totalCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const sub =
    accounts.length === 0
      ? "Set up monitoring"
      : critical > 0
        ? `⚠️ ${critical} critical`
        : anyAlerts
          ? `${accounts.length} accounts · alerts`
          : `${accounts.length} accounts`;
  return (
    <StatCard
      label="API Spend MTD"
      value={value}
      sub={sub}
      href="/api-spend"
      urgent={critical > 0}
      accent={critical === 0 && anyAlerts ? undefined : critical === 0 ? "blue" : undefined}
    />
  );
}

function StatCard({
  label, value, sub, href, urgent, accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  href: string;
  urgent: boolean;
  accent?: "blue";
}) {
  return (
    <Link to={href as any} className="no-underline">
      <div className={`bg-white dark:bg-gray-900 border rounded-lg px-4 py-4 shadow-sm dark:shadow-none hover:shadow-md transition-shadow cursor-pointer ${
        urgent   ? "border-amber-300 dark:border-amber-700" :
        accent === "blue" ? "border-blue-300 dark:border-blue-800" :
        "border-slate-300 dark:border-gray-800"
      }`}>
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">{label}</p>
        <p className={`text-2xl font-bold ${
          urgent ? "text-amber-600 dark:text-amber-400" :
          accent === "blue" ? "text-blue-700 dark:text-blue-300" :
          "text-slate-900 dark:text-slate-100"
        }`}>
          {value}
        </p>
        {sub && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </Link>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-300 dark:border-gray-800 shadow-sm dark:shadow-none divide-y divide-slate-50 dark:divide-gray-800 overflow-hidden">
      {children}
    </div>
  );
}

function SectionHeading({ children, href }: { children: React.ReactNode; href?: string }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        {children}
      </h2>
      {href && (
        <Link to={href as any} className="text-xs text-blue-600 dark:text-blue-400 hover:underline no-underline">
          View all
        </Link>
      )}
    </div>
  );
}

function BankAccountRow({ account }: { account: BankAccount }) {
  const balance = parseFloat(account.current_balance ?? "0");
  return (
    <Link
      to="/bank-accounts"
      className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors no-underline group"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{account.name}</p>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 capitalize">
          {account.account_type.replace(/_/g, " ")}
          {account.account_number_last4 ? ` ···${account.account_number_last4}` : ""}
        </p>
      </div>
      <span className={`text-sm font-semibold tabular-nums flex-shrink-0 ml-3 ${balance < 0 ? "text-red-600 dark:text-red-400" : "text-slate-900 dark:text-slate-100"}`}>
        {fmt(balance)}
      </span>
    </Link>
  );
}

function ActionDot({ action }: { action: string }) {
  const cls =
    action === "INSERT" ? "bg-green-400"  :
    action === "DELETE" ? "bg-red-400"    :
    "bg-blue-400";
  return <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${cls}`} />;
}

function RecentActivityItem({ row }: { row: AuditLogRow }) {
  const href = auditActivityHref(row.table_name, row.row_id);
  const recordLabel = auditRecordLabel(row);
  return (
    <Link
      to={href as any}
      className="flex items-start gap-3 px-4 py-2.5 border-b last:border-0 border-slate-50 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors no-underline cursor-pointer"
    >
      <ActionDot action={row.action} />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-700 dark:text-slate-300 truncate">
          <span className="font-medium">{auditTableLabel(row.table_name)}</span>
          {recordLabel && (
            <span className="text-slate-500 dark:text-slate-400"> · {recordLabel}</span>
          )}
          {" "}
          <span className="text-slate-400 dark:text-slate-500 text-xs">
            {auditActionPast(row.action)}
          </span>
        </p>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{timeAgo(row.at)}</p>
      </div>
    </Link>
  );
}
